import { useEffect, useState, useCallback } from 'react';
import type { ChartItem } from '@/types/api';
import { cropApi } from '@/services/cropApi';
import MonitorChart from '@/components/feature/MonitorChart';

interface ChartPanelProps {
  deviceId: string;
  refreshTrigger?: number;
}

function formatCaptureTime(t: string): string {
  if (!t || t.length < 12) return t;
  return (
    t.substring(0, 2) + '.' +
    t.substring(2, 4) + '.' +
    t.substring(4, 6) + ' ' +
    t.substring(6, 8) + ':' +
    t.substring(8, 10) + ':' +
    t.substring(10, 12)
  );
}

function getTodayStr(): string {
  const d = new Date();
  const yy = String(d.getFullYear());
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}${mm}${dd}`;
}

function downloadCSV(filename: string, headers: string[], rows: string[][]) {
  const BOM = '\uFEFF';
  const csv = BOM + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export default function ChartPanel({ deviceId, refreshTrigger }: ChartPanelProps) {
  const [items, setItems] = useState<ChartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!deviceId) return;
    setLoading(true);
    setError('');
    cropApi
      .getChart(deviceId, 30)
      .then(res => {
        if (res.result === 'success') {
          setItems(res.chart);
        } else {
          setError('차트 데이터를 불러오지 못했습니다.');
        }
      })
      .catch(() => setError('차트 데이터 요청 실패'))
      .finally(() => setLoading(false));
  }, [deviceId, refreshTrigger]);

  const handleDownloadNdvi = useCallback(() => {
    const today = getTodayStr();
    const filename = `NDVI_추이_${deviceId}_${today}.csv`;
    const headers = ['No', '촬영시각', 'NDVI평균', '수신시각'];
    const rows = items.map(r => [
      String(r.no),
      formatCaptureTime(r.capture_time),
      Number(r.ndvi_avg).toFixed(4),
      r.regdate,
    ]);
    downloadCSV(filename, headers, rows);
  }, [items, deviceId]);

  const handleDownloadTof = useCallback(() => {
    const today = getTodayStr();
    const filename = `ToF_벼높이_추이_${deviceId}_${today}.csv`;
    const headers = ['No', '촬영시각', 'ToF거리(mm)', '벼높이(mm)', '수신시각'];
    const rows = items.map(r => [
      String(r.no),
      formatCaptureTime(r.capture_time),
      Number(r.tof_center_mm).toFixed(0),
      Number(r.rice_height_mm).toFixed(0),
      r.regdate,
    ]);
    downloadCSV(filename, headers, rows);
  }, [items, deviceId]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div
        className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2"
        style={{ backgroundColor: '#f0f9f4' }}
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-line-chart-line text-green-600"></i>
        </div>
        <span className="font-bold text-gray-800 text-sm">추이 차트 (최근 30건)</span>
        {loading && (
          <div className="w-4 h-4 flex items-center justify-center ml-1">
            <i className="ri-loader-4-line animate-spin text-gray-400 text-sm"></i>
          </div>
        )}
      </div>

      <div className="p-4 md:p-5">
        {error ? (
          <div className="text-center py-8 text-red-400 text-sm">
            <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            {error}
          </div>
        ) : loading ? (
          <div className="flex flex-col md:flex-row gap-5">
            {[0, 1].map(i => (
              <div key={i} className="flex-1 min-w-0">
                <div className="h-[220px] bg-gray-100 rounded-xl animate-pulse"></div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
              <i className="ri-inbox-line text-2xl"></i>
            </div>
            차트 데이터가 없습니다.
          </div>
        ) : (
          <div className="flex flex-col md:flex-row gap-5">
            {/* NDVI 차트 */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 flex items-center gap-1">
                  <span
                    className="inline-block w-3 h-3 rounded-full"
                    style={{ backgroundColor: '#27ae60' }}
                  ></span>
                  NDVI 평균 추이
                </p>
                <button
                  onClick={handleDownloadNdvi}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-white text-xs font-medium cursor-pointer whitespace-nowrap transition-opacity hover:opacity-80"
                  style={{ backgroundColor: '#27ae60' }}
                  title="NDVI 데이터 CSV 다운로드"
                >
                  <i className="ri-download-2-line text-xs"></i>
                  엑셀 다운로드
                </button>
              </div>
              <MonitorChart items={items} type="ndvi" />
            </div>

            {/* ToF / 벼높이 차트 */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 flex items-center gap-1">
                  <span
                    className="inline-block w-3 h-3 rounded-full"
                    style={{ backgroundColor: '#2E86C1' }}
                  ></span>
                  ToF 거리 / 벼 높이 추이
                </p>
                <button
                  onClick={handleDownloadTof}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-white text-xs font-medium cursor-pointer whitespace-nowrap transition-opacity hover:opacity-80"
                  style={{ backgroundColor: '#27ae60' }}
                  title="ToF/벼높이 데이터 CSV 다운로드"
                >
                  <i className="ri-download-2-line text-xs"></i>
                  엑셀 다운로드
                </button>
              </div>
              <MonitorChart items={items} type="tof" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
